import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";

import {Button, Label, TextError, TitleH1, TitleH3} from "components/generic";
import {IPerson, IPropsFC, RoleUser, TExperience, TGender,} from "interfaces/intarfaces";

import {ContainerGeneric, ContainerNotice, ContainerValue, ContainerValueExtra, FlexBox,} from "components/containers";
import {useInput} from "components/Input";
import {useCalendar} from "components/Calendar";
import {useTextArea} from "components/TextArea";
import {useRange} from "components/Range";
import {useSelect} from "components/Select";
import {useCheckbox, useRadiobox} from "components/CheckRadiobox";
import {getLanguage} from "utils/fetchMapInfo";
import {useMultiSelect} from "components/MultiSelect";
import {UserAPI} from "API/userApi";

import {useContextHospi} from "context/ContextHospi";
import {API_USERS, STUDENT_PROFILEVIEW_ROUTE} from "utils/constants";
import {SocialPreferences, UnauthorizedUser,} from "pages/ProfileUser/SimpleComponents";
import {PersonalDetailsEdit} from "pages/ProfileUser/PersonalDetailsEdit";
import {SearchCriteria} from "pages/ProfileUser/SearchCriteria";
import {AboutMeComponent} from "pages/ProfileUser/AboutMeComponent";
import {isEmptyItem, isoDate} from "utils/helpers";
import {AvatarCroper} from "components/AvatarCroper";
import {LoaderCustom} from "components/Loader";
import {theme} from "styles/theme";
import {uploadAvatar} from "utils/uploadAvatar";
import {TLang} from "assets/data/isoNameLanguage";
import {Comparator} from "../../utils/logics";

const LeftLabel: React.FC<{ keyItem: string }> = ({ keyItem }) => {
  const { t } = useTranslation("student");

  return (
    <ContainerNotice>
      <h3>{t(`studentProfile.${keyItem}.title`)}</h3>
      <p>{t(`studentProfile.${keyItem}.comment`)}</p>
    </ContainerNotice>
  );
};

type TAnchor =
  | "personalDetailsAnchor"
  | "searchCriteriaAnchor"
  | "aboutMeAnchor"
  | "pictureAnchor"
  | null;

export const StudentProfile: React.FC = () => {
  const { t, i18n } = useTranslation("student");
  const navigate = useNavigate();
  const [responseError, setResponseError] = useState("");
  const [response401, setResponse401] = useState(false);
  const [requiredField, setRequiredField] = useState(false);

  const { userProfile, updateProfile, getCurrentUserID } =
    useContextHospi();

  const [errorAnchor, setErrorAnchor] = useState<TAnchor>(null);
  const [loading, setLoading] = useState(false);
  const textLoader = t("textLoading.1");

  const checkItemExistsInArray = (item: TExperience): boolean | undefined => {
    return (
      userProfile &&
      userProfile?.past_experience &&
      userProfile?.past_experience.indexOf(item) !== -1
    );
  };

  const titlePholder = (part: string, variable: string): IPropsFC => {
    return {
      title: t(`studentProfile.${part}.form.${variable}.title`),
      placeholder: t(`studentProfile.${part}.form.${variable}.placeholder`),
      w: "initial",
      grow: "1",
    };
  };

  const firstName = {
    ...useInput(userProfile?.first_name || ""),
    name: "first-name",
    required: requiredField,
  };
  const lastName = {
    ...useInput(userProfile?.last_name || ""),
    name: "last-name",
    required: requiredField,
  };
  const dateBirth = {
    ...useInput(
      userProfile?.birth_date ? isoDate(new Date(userProfile?.birth_date)) : ""
    ),
    name: "bday",
    required: requiredField,
  };

  const birthCountry = {
    ...useSelect(userProfile?.birth_country || undefined),
    required: requiredField,
  };
  const phoneCode = {
    ...useSelect(userProfile?.phone_code || undefined),
    required: requiredField,
  };
  const phone = {
    ...useInput(userProfile?.phone_number || "", 15),
    name: "tel-local",
    required: requiredField,
  };

  const dateIn = {
    ...useCalendar({
      value: userProfile?.preferred_move_in
        ? new Date(userProfile?.preferred_move_in)
        : undefined,
      range: false,
    }),
    required: requiredField,
  };

  const dateOut = {
    ...useCalendar({
      value: userProfile?.preferred_move_out
        ? new Date(userProfile?.preferred_move_out)
        : undefined,
      range: false,
    }),
    required: requiredField,
  };

  const budget = {
    ...useInput(userProfile?.max_budget || ""),
    required: requiredField,
  };

  const countryResidence = useSelect(
    "NL"
    // This commit still only NL
    // userProfile?.city_of_residence?.state || ""
  );

  const stateResidence = {
    ...useSelect(userProfile?.city_of_residence?.province || undefined),
    required: requiredField,
  };

  const cityResidence = {
    ...useSelect(userProfile?.city_of_residence?.city || undefined),
    required: requiredField,
  };

  const gender = {
    ...useRadiobox("gender", userProfile?.gender || ""),
    name: "gender",
  };

  const study = {
    ...useInput(userProfile?.study || ""),
    ...titlePholder("aboutMeInfo", "study"),
    required: requiredField,
  };
  const spokenLanguage = {
    ...useMultiSelect(
      getLanguage(i18n.language.toUpperCase() as TLang),
      userProfile?.spoken_languages || [],
      ["gb", "nl"]
    ),
    ...titlePholder("aboutMeInfo", "spokenLanguage"),
    required: requiredField,
  };
  const livedWithHost = useCheckbox(checkItemExistsInArray("Lived with host"));
  const livedAbroad = useCheckbox(checkItemExistsInArray("Lived abroad"));
  const livedOwn = useCheckbox(checkItemExistsInArray("Lived on my own"));
  const livedAny = useCheckbox(checkItemExistsInArray("None of the above"));

  const shortDescription = {
    ...useTextArea(userProfile?.description || ""),
    ...titlePholder("aboutMeInfo", "aboutYourself"),
    required: requiredField,
  };

  const privacy = useRange(userProfile?.privacy || 50);
  const newInTown = useRange(userProfile?.autonomy || 50);

  const [photo, setPhoto] = useState<File | undefined>(undefined);

  useEffect(() => {
    const checkFillAllFields = () => {
      if (
        [
          String(firstName.value).length,
          String(lastName.value).length,
          dateBirth.value,
          birthCountry.value,
          phoneCode.value,
          String(phone.value).length,
        ].some(isEmptyItem)
      ) {
        setErrorAnchor("personalDetailsAnchor");
      } else if (
        [
          dateIn.value,
          dateOut.value,
          String(budget.value).length,
          countryResidence.value,
          stateResidence.value,
          cityResidence.value,
        ].some(isEmptyItem)
      ) {
        setErrorAnchor("searchCriteriaAnchor");
      } else if (
        [
          gender.selected.length,
          String(study.value).length,
          spokenLanguage.values.length,
          shortDescription.value.length,
        ].some(isEmptyItem)
      ) {
        setErrorAnchor("aboutMeAnchor");
      } else if (!photo) {
        setErrorAnchor("pictureAnchor");
      } else {
        setErrorAnchor(null);
      }
    };
    checkFillAllFields();
  });

  const sendFromData = async () => {
    //@ts-ignore
    const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));

    if (errorAnchor !== null) {
      setRequiredField(true);
      setResponseError(
        errorAnchor === "pictureAnchor"
          ? t(`studentProfile.errormessage.imageforgot`)
          : t(`studentProfile.errormessage.allfields`)
      );
      const el = document.getElementById(errorAnchor);
      el?.scrollIntoView({ behavior: "smooth" });
    } else {
      setLoading(true);
      const formPastExperience: TExperience[] = [];

      livedWithHost.checked && formPastExperience.push("Lived with host");
      livedAbroad.checked && formPastExperience.push("Lived abroad");
      livedOwn.checked && formPastExperience.push("Lived on my own");
      livedAny.checked && formPastExperience.push("None of the above");

      let safariBirthday;
      safariBirthday =
        (dateBirth.value as string).replace(/[-/.]/g, '-').split('-');

      safariBirthday = safariBirthday[0].length === 4 ? safariBirthday.join('-') : safariBirthday[2] + '-' + safariBirthday[1] + '-' + safariBirthday[0]

      const userFormData: Partial<IPerson> = {
        user_id: getCurrentUserID,
        first_name: firstName.value as string,
        last_name: lastName.value as string,
        phone_code: phoneCode.value as string,
        phone_number: phone.value as string,
        gender: gender.selected as TGender,
        spoken_languages: spokenLanguage.values,
        default_language: i18n.language,
        description: shortDescription.value as string,
        privacy: privacy.value,
        user_type: "student" as RoleUser,
        birth_date: (isSafari ? safariBirthday : (dateBirth.value as string)) ?? "",
        birth_country: birthCountry.value as string,
        preferred_move_in: isoDate(dateIn.value),
        preferred_move_out: isoDate(dateOut.value),
        max_budget: (budget.value || 0) as number,
        city_of_residence: {
          country: (countryResidence.value as string) || "",
          province: (stateResidence.value as string) ?? "",
          city: (cityResidence.value as string) ?? "",
        },
        study: String(study.value) as string,
        past_experience: formPastExperience.length
          ? formPastExperience
          : ["None of the above"],
        autonomy: newInTown.value,
      };

      const response = userProfile?.user_id ? await UserAPI.update(API_USERS + `student/${userProfile?.user_id}`, {
        ...Comparator(userFormData, userProfile, 'student'),
        user_type: "student",
      }) : await UserAPI.create(
        API_USERS + "student/",
        userFormData
      );

      if (response.hasOwnProperty("status")) {
        if (response.status === 401) setResponse401(true);
        else
          try {
            setResponseError(
              response.errorMessages
                .map((line: [string, string]) => `${line[0]} - ${line[1]}`)
                .join("\n")
            );
          } catch (error) {
            setResponseError("Error. Check your fields.");
          }
        setLoading(false);
        return;
      }

      const [thumbnail_url, is_profile_complete] = await uploadAvatar(
        photo,
        getCurrentUserID
      );
      updateProfile({
        ...(response as IPerson),
        thumbnail_url: !!thumbnail_url.length ? thumbnail_url : undefined,
        is_profile_complete,
      });
      setLoading(false);
      navigate(`/${i18n.language}${STUDENT_PROFILEVIEW_ROUTE.slice(5)}`);
    }
  };

  // When user Unauthorized
  if (response401) return <UnauthorizedUser />;

  return (
    <ContainerGeneric>
      <form />
      <TitleH1>{t("studentProfile.title")}</TitleH1>
      <LeftLabel keyItem="registerAs" />
      {loading && (
        <LoaderCustom
          size={5}
          color={theme.colors.bgBody}
          text={textLoader}
        />
      )}

      <ContainerValue>
        <FlexBox justify="space-between" gap="24px">
          {/* <BorderBox active={true}>
            <Radiobox
              p="1.5rem"
              checked={true}
              selected="student"
              title={t("studentProfile.registerAs.form.student")}
              value="student"
              h="64px"
            />
          </BorderBox>

          <BorderBox active={false} onClick={() => changeUser("host")}>
            <Radiobox
              p="1.5rem"
              title={t("studentProfile.registerAs.form.host")}
              value="host"
              h="64px"
            />
          </BorderBox> */}

          <Label>
            {t("studentProfile.introtext.intro")}
          </Label>
        </FlexBox>
      </ContainerValue>
      <LeftLabel keyItem="personalDetails" />
      <ContainerValue id="personalDetailsAnchor">
        <PersonalDetailsEdit
          firstName={firstName}
          lastName={lastName}
          dateBirth={dateBirth}
          birthCountry={{ ...birthCountry, required: requiredField }}
          phoneCode={phoneCode}
          phone={phone}
        />
      </ContainerValue>
      <LeftLabel keyItem="searchCriteria" />
      <ContainerValue id="searchCriteriaAnchor">
        <SearchCriteria
          dateIn={dateIn}
          dateOut={dateOut}
          budget={budget}
          countryResidence={countryResidence}
          provinceResidence={stateResidence}
          cityResidence={cityResidence}
        />
      </ContainerValue>
      <LeftLabel keyItem="aboutMeInfo" />
      <ContainerValue id="aboutMeAnchor">
        <AboutMeComponent
          gender={gender}
          study={study}
          spokenLanguage={spokenLanguage}
          expirience={[livedWithHost, livedAbroad, livedOwn, livedAny]}
          shortDescription={shortDescription}
        />
      </ContainerValue>
      <LeftLabel keyItem="socialPreferences" />
      <ContainerValue>
        <SocialPreferences
          roleUser="student"
          privacy={privacy}
          newInTown={newInTown}
          muted={false}
        />
      </ContainerValue>

      <LeftLabel keyItem="profilePicture" />
      <ContainerValue id="pictureAnchor">
        <TitleH3 style={{ marginBottom: "16px" }}>
          {t(`studentProfile.profilePicture.form.photo.title`)}
        </TitleH3>
        <AvatarCroper getAvatar={setPhoto} />
      </ContainerValue>
      {/* {responseError.length > 0 && (
        <ContainerValue>
          <TextError>{responseError}</TextError>{" "}
        </ContainerValue>
      )} */}

      <span />
      <ContainerValueExtra>
        {responseError.length > 0 && (
          <ContainerValue>
            <TextError>{responseError}</TextError>{" "}
          </ContainerValue>
        )}
        <FlexBox justify="flex-end" margin="0 0 24px 0">
          <Button main onClick={() => sendFromData()}>
            {t(`studentProfile.buttonSave.title`)}
          </Button>
        </FlexBox>
      </ContainerValueExtra>

    </ContainerGeneric>
  );
};
